import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Plus, User, ShieldCheck, Search } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const scrollToSection = (id) => {
  if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -80 });
  else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const isStaff = user && ["employee", "admin"].includes(user.role);

  const goSection = (id) => {
    if (loc.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(id), 350);
    } else scrollToSection(id);
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-40 border-b border-black/5 bg-bone/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo-link" aria-label="Alsat67 ana sayfa">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-semibold">
            <button data-testid="nav-listings-link" onClick={() => goSection("ilanlar")} className="hover:text-coral transition-colors">İlanlar</button>
            <button data-testid="nav-categories-link" onClick={() => goSection("kategoriler")} className="hover:text-coral transition-colors">Kategoriler</button>
            <button data-testid="nav-how-link" onClick={() => goSection("nasil-calisir")} className="hover:text-coral transition-colors">Nasıl Çalışır</button>
          </nav>
          <div className="flex items-center gap-2">
            {user === null ? null : user ? (
              <>
                {isStaff && (
                  <Link to="/panel" data-testid="nav-panel-link" className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-coal/15 px-4 py-2 text-sm font-semibold hover:border-coral hover:text-coral transition-colors">
                    <ShieldCheck size={16} /> Panel
                  </Link>
                )}
                <Link to="/hesabim" data-testid="nav-account-link" className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-coal/15 px-4 py-2 text-sm font-semibold hover:border-coral hover:text-coral transition-colors">
                  <User size={16} /> Hesabım
                </Link>
                <button
                  data-testid="nav-logout-button"
                  onClick={async () => { await logout(); toast.success("Çıkış yapıldı."); navigate("/"); }}
                  className="hidden sm:grid place-items-center rounded-full border border-coal/15 p-2.5 hover:border-coral hover:text-coral transition-colors"
                  aria-label="Çıkış yap"
                >
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <Link to="/giris" data-testid="nav-login-link" className="rounded-full px-4 py-2 text-sm font-semibold hover:text-coral transition-colors">Giriş Yap</Link>
            )}
            <Link
              to="/ilan-ver"
              data-testid="create-listing-open-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-coral px-4 sm:px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-coral/25 hover:bg-coraldark hover:-translate-y-0.5 transition-all"
            >
              <Plus size={16} strokeWidth={3} /> İlan Ver
            </Link>
          </div>
        </div>
      </header>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-black/10 bg-bone/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-4 h-16">
          <button data-testid="mobile-nav-home" onClick={() => goSection("ilanlar")} className="flex flex-col items-center justify-center gap-0.5 text-xs font-semibold text-slate-600">
            <Search size={20} /> Keşfet
          </button>
          <Link to="/giris" data-testid="mobile-nav-account" className="flex flex-col items-center justify-center gap-0.5 text-xs font-semibold text-slate-600">
            <User size={20} /> {user ? "Hesabım" : "Giriş"}
          </Link>
          <Link to="/ilan-ver" data-testid="mobile-nav-create" className="flex flex-col items-center justify-center gap-0.5 text-xs font-semibold">
            <span className="grid place-items-center -mt-5 h-12 w-12 rounded-2xl bg-coral text-white shadow-lg shadow-coral/40">
              <Plus size={24} strokeWidth={3} />
            </span>
            <span className="text-coral">İlan Ver</span>
          </Link>
          {isStaff ? (
            <Link to="/panel" data-testid="mobile-nav-panel" className="flex flex-col items-center justify-center gap-0.5 text-xs font-semibold text-slate-600">
              <ShieldCheck size={20} /> Panel
            </Link>
          ) : (
            <Link to="/hesabim" data-testid="mobile-nav-hesabim" className="flex flex-col items-center justify-center gap-0.5 text-xs font-semibold text-slate-600">
              <User size={20} /> İlanlarım
            </Link>
          )}
        </div>
      </nav>
    </>
  );
}
